/**
 * Centralized copy/text constants for MatchaMap
 *
 * All user-facing strings should be defined here to:
 * - Make copy updates easier across the entire app
 * - Enable future internationalization (i18n)
 * - Provide type-safety via TypeScript
 * - Make copy audits/searches easier
 *
 * Organization: Grouped by feature/component
 */

export const COPY = {
  // Header & Navigation
  header: {
    title: 'MatchaMap',
    instagramAriaLabel: 'Instagram',
    tiktokAriaLabel: 'TikTok',
    menuAriaLabel: 'Menu',
  },

  // Menu Items
  menu: {
    signIn: 'Sign In',
    signOut: 'Sign Out',
    myProfile: 'My Profile',
    about: 'About',
    shop: 'Shop',
    contact: 'Contact',
    settings: 'Settings',
  },

  // Map View
  map: {
    directions: 'Directions',
    viewDetails: 'Details',
    viewFullDetails: 'Details',
    getDirections: 'Get Directions',
    backToMap: 'Back to Map',
    enableLocationServices: 'Enable location services',
    findLocation: 'Find my location',
    locationFound: 'Location found',
    drinks: 'Drinks',
    hours: 'Hours',
    today: 'Today',
    follow: 'Follow',
    openNow: 'Open Now',
    closedNow: 'Closed',
    underPrice: (price: number) => `Under $${price}`,
    ratingFilter: (rating: number) => `${rating}+ ★`,
    drinkType: 'Drink Type',
    allDrinks: 'All Drinks',
    clearFilters: 'Clear filters',
    allCities: 'All Cities',
    showRoute: 'Show Route',
    hideRoute: 'Hide Route',
    routeLoading: 'Loading route...',
    routeError: 'Unable to load route',
    walkingTime: (duration: string) => `${duration} walk`,
    walkingDistance: (distance: string) => distance,
    viewInstagramReview: 'View Instagram review',
    viewTikTokReview: 'View TikTok review',
    seeInstagramReel: 'Reel',
    seeTikTokReview: 'TikTok',
  },

  // Location Dialogs
  location: {
    permissionDenied: {
      title: 'Location Access Needed',
      description: 'We need location access to calculate distances to cafes. Your location is never stored or shared.',
      tryAgain: 'Try Again',
      skip: 'Skip for Now',
    },
    loading: {
      title: 'Finding Your Location',
      description: 'Please allow location access when prompted. This may take a few seconds...',
      cancel: 'Cancel',
    },
    unavailable: {
      title: 'Location Unavailable',
      close: 'Close',
      tryAgain: 'Try Again',
      errorMessages: {
        positionUnavailable: 'Unable to determine your location. Try moving to an open area with better GPS signal, or make sure location services are enabled for your browser.',
        timeout: 'Location request timed out. This often happens indoors or in areas with poor GPS signal. Try again when you have better signal or are outdoors.',
        notSupported: 'Geolocation is not supported by your browser. Try using a modern browser like Chrome, Safari, or Firefox.',
        generic: 'Location services are not available. Make sure you\'re on a secure (HTTPS) connection and location services are enabled.',
      },
    },
  },

  // List View
  list: {
    sortByRating: 'Sort: Rating',
    sortByDistance: 'Sort: Distance',
    search: 'Search',
    filter: 'Filter',
    city: 'City',
    searchPlaceholder: 'Search cafes, reviews, tags, or neighborhoods...',
    clearSearch: 'Clear search',
    minimumRating: 'Minimum Rating',
    userMinRating: 'Min User Rating',
    userMaxRating: 'Max User Rating',
    userRatingRange: 'User Rating',
    maxDistance: 'Max Distance',
    maxDistanceRequiresLocation: 'Max Distance (Enable location first)',
    drinkType: 'Drink Type',
    allDrinks: 'All Drinks',
    allCities: 'All Cities',
    availability: 'Availability',
    clearAllFilters: 'Clear All Filters',
    clearFilters: 'Clear filters',
    noResults: 'No cafes match your search or filters',
    tapLocationForDistance: 'Tap location for distance',
    reviewSnippets: 'Matching Reviews',
    fromReview: 'From review',
    walkTime: (distance: string, time: string) => `${distance} • ${time} walk`,
    kmDistance: (km: number) => `${km}km`,
    filterByCity: 'Filter by City',
    citiesSelected: (count: number) => `${count} cities selected`,
    clearAll: 'Clear All',
    viewInstagramReview: 'View Instagram review',
    viewTikTokReview: 'View TikTok review',
  },

  // Detail View
  detail: {
    getDirections: 'Get Directions',
    visited: 'Visited! ✓',
    markVisited: 'Mark as visited (Matcha Passport)',
    drinksMenu: 'Drinks Menu',
    featured: '⭐ Featured',
    cafeDetails: 'Cafe Details',
    ambiance: 'Ambiance',
    alternativeMilk: 'Alternative Milk',
    alternativeMilkFree: 'Free ✓',
    alternativeMilkCharge: (price: number) => `+$${price.toFixed(2)}`,
    alternativeMilkUnknown: 'Unknown',
    ourReview: 'Our Review',
    ourReviews: 'Our Reviews',
    hours: 'Hours',
    today: 'Today',
    showFullWeek: 'Show full week',
    showLess: 'Show less',
    quickNote: 'Quick Note',
    socialMedia: 'Social Media',
    follow: 'Follow',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    matchaAmount: (grams: number) => `${grams}g matcha`,
    icedMatchaLatte: 'Iced Matcha Latte',
    awayDistance: (distance: string, time: string) => `${distance} away • ${time} walk`,
    scoreOutOf: '/ 10',
    seeInstagramReel: 'Reel',
    seeTikTokReview: 'TikTok',
    closedNow: 'Closed',
  },

  // Passport View
  passport: {
    title: 'Matcha Passport',
    subtitle: 'Track your matcha journey across Toronto',
    visited: 'Visited',
    total: 'Total',
    clearAll: 'Clear All',
    confirmClear: 'Are you sure you want to clear all your visited locations?',
    cancel: 'Cancel',
    confirm: 'Clear',
    noVisited: 'No cafes visited yet',
    startExploring: 'Start exploring to build your matcha passport!',
    visitedOn: (date: string) => `Visited on ${date}`,
    addNote: 'Add Note',
    notes: 'Notes',
    migrationModal: {
      title: 'Sync Your Passport',
      description: 'We found visits saved locally on your device. Would you like to sync them to your account?',
      localVisits: (count: number) => `${count} local visit${count !== 1 ? 's' : ''} found`,
      migrate: 'Sync to Account',
      skip: 'Skip for Now',
      migrating: 'Syncing...',
      success: 'Successfully synced your visits!',
      error: 'Failed to sync visits. Please try again.',
    },
  },


  // Events View
  events: {
    title: 'Events',
    subtitle: 'Toronto matcha community gatherings & workshops',
    noEvents: 'No upcoming events',
    checkBackSoon: 'Check back soon for matcha events!',
    featuredEvent: 'Featured Event',
    viewCafe: 'View Cafe',
    viewDetails: 'View Details',
    goingCount: (count: number) => `${count} matchamappers are going!`,
    eventDetails: 'Event Details',
    date: 'Date',
    time: 'Time',
    venue: 'Venue',
    location: 'Location',
    price: 'Price',
    description: 'Description',
    hostedAt: 'Hosted At',
    viewOnInstagram: 'View on Instagram',
    getDirections: 'Get Directions',
    showPastEvents: 'Show Past Events',
    hidePastEvents: 'Hide Past Events',
    failedToLoadPastEvents: 'Failed to load past events. Please try again.',
    networkError: 'Network error. Please check your connection and try again.',
  },

  // Contact Page
  contact: {
    pageTitle: 'Contact Us',
    pageSubtitle: 'Have a question or want to suggest a cafe?',
    nameLabel: 'Name',
    emailLabel: 'Email',
    subjectLabel: 'Subject',
    messageLabel: 'Message',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'your@email.com',
    messagePlaceholder: 'Tell us more...',
    selectTopic: 'Select a topic...',
    topicCafeSuggestion: 'Suggest a Cafe',
    topicCorrection: 'Report an Error',
    topicGeneral: 'General Inquiry',
    topicPartnership: 'Partnership Opportunity',
    topicFeedback: 'Feedback',
    send: 'Send Message',
    sending: 'Sending...',
    successTitle: 'Message Sent!',
    success: 'Thanks for reaching out. We\'ll get back to you soon.',
    error: 'Failed to send message. Please try again.',
    emailSupportHeading: 'Email',
    emailSupportAddress: 'hello@matchamap.com',
    socialHeading: 'Social',
    socialHandle: '@vivisual.diary',
  },

  // About Page
  about: {
    title: 'About MatchaMap',
    subtitle: 'Your guide to Toronto\'s best matcha cafes',
  },

  // Store Page
  store: {
    title: 'Shop',
    comingSoon: 'Coming Soon',
  },

  // Settings Page
  settings: {
    title: 'Settings',
    preferences: 'Preferences',
    account: 'Account',
  },

  // Coming Soon
  comingSoon: {
    title: 'Coming Soon',
    description: 'This feature is under development. Check back soon!',
  },

  // Bottom Navigation
  nav: {
    map: 'Map',
    list: 'List',
    passport: 'Passport',
    events: 'Events',
  },

  // Common/Shared
  common: {
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    update: 'Update',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    viewMore: 'View More',
    viewLess: 'View Less',
    seeAll: 'See All',
    unknown: 'Unknown',
  },

  // Authentication
  auth: {
    // Login Page
    welcomeBack: 'Welcome Back',
    joinCommunity: 'Join the Community',
    adminAccess: 'Admin Access',
    signInToSave: 'Sign in to save your matcha journey',
    createAccountToTrack: 'Create an account to track cafes and write reviews',
    signInForAdmin: 'Sign in to access the admin panel',
    adminAccessRestricted: 'Access restricted to authorized administrators',

    // Login Form
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    dontHaveAccount: "Don't have an account? Sign up",

    // Register Form
    username: 'Username',
    usernamePlaceholder: 'matchalover',
    usernameHint: 'Letters, numbers, and underscores only',
    confirmPassword: 'Confirm Password',
    createAccount: 'Create Account',
    creatingAccount: 'Creating Account...',
    alreadyHaveAccount: 'Already have an account? Sign in',
    passwordMinLength: 'At least 8 characters',

    // Validation Errors
    passwordTooShort: 'Password must be at least 8 characters long',
    passwordsDoNotMatch: 'Passwords do not match',
    usernameTooShort: 'Username must be at least 3 characters long',
    usernameInvalidChars: 'Username can only contain letters, numbers, and underscores',

    // Session Expiration
    sessionExpired: {
      title: 'Session Expired',
      message: 'Your session has expired. Please sign in again to continue.',
      signInAgain: 'Sign In Again',
      dismiss: 'Dismiss',
    },
  },

  // Profile
  profile: {
    editProfile: 'Edit Profile',
    loadingProfile: 'Loading profile...',
    profileNotFound: 'Profile Not Found',
    edit: 'Edit',
    website: 'Website',
    memberSince: 'Member since',

    // Welcome Banner
    welcomeTitle: 'Welcome to MatchaMap! 🍵',
    welcomeSubtitle: "You're all set! Here's how to get started on your matcha journey:",
    exploreCafes: 'Explore Cafes',
    exploreCafesDescription: 'Browse our curated list of the best matcha cafes in Toronto',
    buildPassport: 'Build Your Passport',
    buildPassportDescription: 'Visit cafes and mark them on your passport to track your journey',
    startExploringButton: 'Start Exploring',
    dismissButton: 'Dismiss',

    // Edit Profile Modal
    displayName: 'Display Name',
    displayNamePlaceholder: 'Your name',
    bio: 'Bio',
    bioPlaceholder: 'Tell us about yourself...',
    bioCharCount: (current: number, max: number) => `${current}/${max}`,
    location: 'Location',
    locationPlaceholder: 'Toronto, ON',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    usernamePlaceholder: 'username',
    websiteUrl: 'Website',
    websitePlaceholder: 'https://example.com',
    privacySettings: 'Privacy Settings',
    publicProfile: 'Public Profile',
    publicProfileDescription: 'Allow anyone to view your profile',
    showActivity: 'Show Activity',
    showActivityDescription: 'Display your recent reviews and check-ins',
    saving: 'Saving...',
    saveChanges: 'Save Changes',
    failedToUpdate: 'Failed to update profile',

    // Profile Stats
    reviews: 'Reviews',
    checkins: 'Check-ins',
    photos: 'Photos',
    passport: 'Passport',
    reputation: (score: number) => `Reputation: ${score} points`,

    // Profile Activity
    badges: 'Badges',
    recentActivity: 'Recent Activity',
    ownActivityPlaceholder: 'Your recent activity will appear here once you start reviewing cafes!',
    userActivityPlaceholder: (username: string) => `${username}'s recent activity will appear here.`,
    activityPrivate: 'This user\'s activity is private',
    loadingActivity: 'Loading activity...',
    reviewedCafe: 'Reviewed a cafe',
    helpfulCount: (count: number) => `${count} helpful`,
    noBadgesOther: 'No badges earned yet',
    failedToLoadActivity: 'Failed to load activity',
  },

  // Lists (Phase 2E)
  lists: {
    // Lists Overview
    myLists: 'My Lists',
    createList: 'Create List',
    noLists: 'No lists yet',
    noListsDescription: 'Create custom lists to organize your favorite cafes',
    listCount: (count: number) => `${count} ${count === 1 ? 'cafe' : 'cafes'}`,

    // Create/Edit List Modal
    createNewList: 'Create New List',
    editList: 'Edit List',
    listName: 'List Name',
    listNamePlaceholder: 'e.g., Weekend Spots, Study Cafes',
    description: 'Description',
    descriptionPlaceholder: 'Optional description...',
    makePublic: 'Make this list public',
    makePublicDescription: 'Anyone can view this list',
    creating: 'Creating...',
    create: 'Create',
    saving: 'Saving...',
    save: 'Save',
    cancel: 'Cancel',

    // List Detail
    editListButton: 'Edit List',
    deleteListButton: 'Delete List',
    shareList: 'Share List',
    backToLists: 'Back to Lists',
    emptyList: 'No cafes in this list',
    emptyListDescription: 'Add cafes from the map or list view',
    removeCafe: 'Remove from list',
    viewCafe: 'View Details',
    publicBadge: 'Public',
    privateBadge: 'Private',

    // Add to List
    addToList: 'Add to List',
    addingToList: 'Adding...',
    selectList: 'Select a list',
    createNewListOption: '+ Create new list',
    addNotes: 'Add notes (optional)',
    notesPlaceholder: 'Notes about this cafe...',
    add: 'Add',
    alreadyInList: 'Already in this list',
    addedToList: 'Added to list!',

    // Delete Confirmation
    deleteListTitle: 'Delete List?',
    deleteListMessage: (name: string) => `Are you sure you want to delete "${name}"? This cannot be undone.`,
    delete: 'Delete',
    deleting: 'Deleting...',

    // Errors
    failedToCreate: 'Failed to create list',
    failedToUpdate: 'Failed to update list',
    failedToDelete: 'Failed to delete list',
    failedToLoad: 'Failed to load lists',
    failedToAddCafe: 'Failed to add cafe to list',
    failedToRemoveCafe: 'Failed to remove cafe from list',
    nameRequired: 'List name is required',
    nameTooLong: 'List name must be 100 characters or less',
  },

  // Admin (basic strings, admin has more specific copy in components)
  admin: {
    title: 'Admin Dashboard',
    mode: 'Admin Mode',
    cafes: 'Cafes',
    drinks: 'Drinks',
    events: 'Events',
    stats: 'Analytics',
    users: 'Users',
    settings: 'Settings',
    moderation: 'Moderation',

    // Analytics Dashboard
    analytics: {
      title: 'Analytics Dashboard',
      subtitle: 'Partner-actionable metrics and insights',

      // User Activity Section
      userActivity: 'User Activity',
      totalUsers: 'Total Users',
      activeUsers7d: 'Active (7d)',
      activeUsers30d: 'Active (30d)',
      totalCheckins: 'Total Check-ins',
      repeatVisitors: 'Repeat Visitors',

      // Cafe Performance Section
      cafePerformance: 'Cafe Performance',
      totalViews: 'Total Views',
      totalDirections: 'Directions',
      totalAnonymousMarks: 'Anonymous Marks',
      totalAuthCheckins: 'Auth Check-ins',
      totalDemand: 'Total Demand',
      avgCtr: 'Avg CTR',

      // Table Headers
      cafe: 'Cafe',
      city: 'City',
      views: 'Views',
      directions: 'Directions',
      ctr: 'CTR',
      anonymousMarks: 'Anon Marks',
      checkins: 'Check-ins',
      demand: 'Demand',
      social: 'Social',

      // Tooltips
      demandTooltip: 'Anonymous marks + authenticated check-ins',
      ctrTooltip: 'Click-through rate: directions / views',
      /** Accessible label for sortable column header buttons */
      sortTableByColumn: (columnLabel: string) => `Sort by ${columnLabel}`,

      // Loading/Error
      loading: 'Loading analytics...',
      error: 'Failed to load analytics',
      noData: 'No data available yet',
      retry: 'Retry',
    },
    // Error Boundaries
    errorBoundary: {
      title: 'Something went wrong in the admin panel',
      description: "Don't worry - your data is safe. You can try refreshing or go back to continue working.",
      tryAgain: 'Try Again',
      goBack: 'Go Back',
      showDetails: 'Show Technical Details',
      hideDetails: 'Hide Technical Details',
      copyReport: 'Copy Error Report',
      reportIssue: 'Report Issue',
      helpText: 'If this error persists, please copy the error report and contact the development team.',
      componentError: (componentName: string) => `${componentName} Error`,
      componentErrorDescription: 'This component encountered an error and couldn\'t load properly. You can try refreshing this section.',
    },

    // Cafe Editor
    cafeEditor: {
      coordsReadOnly: 'Coordinates cannot be edited here',
      coordsAutoUpdated: 'Coordinates are automatically updated when you refresh from Google Maps',
    },

    // Waitlist Management
    waitlist: {
      title: 'Waitlist Signups',
      subtitle: 'Manage and track early access signups',
      exportCsv: 'Export CSV',
      totalSignups: (count: number) => `${count} total signups`,
      dailySignups: (count: number) => `${count} signups`,
      last24Hours: 'Past 24h',
      weeklySignups: (count: number) => `${count} this week`,
      conversionRate: (rate: number) => `${rate}% conversion rate`,
      email: 'Email',
      signupDate: 'Signup Date',
      referralSource: 'Referral Source',
      status: 'Status',
      converted: 'Converted',
      pending: 'Pending',
      sortByEmail: 'Sort by Email',
      sortByDate: 'Sort by Date',
      sortAscending: 'Sort Ascending',
      sortDescending: 'Sort Descending',
      loading: 'Loading waitlist...',
      loadingMore: 'Loading more...',
      noEntries: 'No waitlist entries found',
      errorLoading: 'Failed to load waitlist entries',
      analytics: 'Analytics',
      entriesPerPage: (limit: number) => `Showing ${limit} entries per page`,
      loadMore: 'Load More',
      unknown: 'Unknown',
      
      // Fraud Detection
      showFraudStats: 'Show Fraud Stats',
      hideFraudStats: 'Hide Fraud Stats',
      suspectedFraud: 'Suspected Fraud',
      fraudReasons: 'Fraud Reasons:',
      filterAll: 'All Signups',
      filterClean: 'Clean Only', 
      filterSuspected: 'Suspected Fraud',
      fraudAnalytics: 'Fraud Analytics',
      suspectedFraudCount: (count: number) => `${count} flagged`,
    },

    // Cafe Management
    cafeManagement: {
      missingFieldsTooltip: (count: number) =>
        `${count} optional field${count !== 1 ? 's' : ''} missing`,
      missingFieldsList: (fields: string[]) =>
        `Missing: ${fields.join(', ')}`,
    },

    // User Management
    userManagement: {
      title: 'User Management',
      searchPlaceholder: 'Search users...',
      filterByRole: 'Filter by Role',
      allRoles: 'All Roles',
      adminRole: 'Admins',
      userRole: 'Users',
      totalUsers: 'Total Users',
      adminUsers: 'Admin Users',
      regularUsers: 'Regular Users',
      activeThisWeek: 'Active This Week',
      newThisMonth: 'New This Month',
      confirmRoleChange: (role: string) => `Are you sure you want to change this user's role to ${role}?`,
      confirmDelete: (username: string) => `Are you sure you want to delete user "${username}"? This action cannot be undone.`,
      roleUpdateSuccess: 'User role updated successfully',
      roleUpdateError: 'Failed to update user role',
      deleteSuccess: 'User deleted successfully',
      deleteError: 'Failed to delete user',
      loadError: 'Failed to load users',
      viewDetails: 'Details',
      makeAdmin: 'Make Admin',
      makeUser: 'Make User',
      deleteUser: 'Delete User',
      email: 'Email',
      username: 'Username',
      role: 'Role',
      status: 'Status',
      lastActive: 'Last Active',
      joined: 'Joined',
      verified: 'Verified',
      unverified: 'Unverified',
      admin: 'Admin',
      user: 'User',
      never: 'Never',
      today: 'Today',
      yesterday: 'Yesterday',
      daysAgo: (days: number) => `${days} days ago`,
      weeksAgo: (weeks: number) => `${weeks} weeks ago`,
    },

    // Moderation Dashboard
    moderationDashboard: {
      title: 'Moderation Dashboard',
      subtitle: 'Review and moderate user-generated content',
      tabs: {
        reviews: 'Reviews',
        photos: 'Photos',
        comments: 'Comments',
        suggestions: 'Suggestions',
      },
      bulkActions: {
        title: 'Bulk Actions',
        selected: (count: number) => `${count} selected`,
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        approveSelected: 'Approve Selected',
        rejectSelected: 'Reject Selected',
        confirmApprove: (count: number) => `Approve ${count} item${count !== 1 ? 's' : ''}?`,
        confirmReject: (count: number) => `Reject ${count} item${count !== 1 ? 's' : ''}?`,
        success: (action: string, count: number) => `${action} ${count} item${count !== 1 ? 's' : ''} successfully`,
        error: 'Failed to perform bulk action',
        processing: 'Processing...',
      },
      stats: {
        pending: (count: number) => `${count} pending`,
        approved: (count: number) => `${count} approved`,
        rejected: (count: number) => `${count} rejected`,
        flagged: (count: number) => `${count} flagged`,
        total: (count: number) => `${count} total`,
      },
      filters: {
        all: 'All',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        flagged: 'Flagged',
        today: 'Today',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
      },
      actions: {
        approve: 'Approve',
        reject: 'Reject',
        flag: 'Flag',
        view: 'View',
        delete: 'Delete',
      },
      placeholders: {
        noReviews: 'No reviews pending moderation',
        noPhotos: 'No photos pending moderation',
        noComments: 'No comments pending moderation',
        noSuggestions: 'No cafe suggestions pending review',
        allReviewed: 'All items have been reviewed',
        comingSoon: 'This feature is coming soon',
      },
      loading: 'Loading moderation queue...',
      error: 'Failed to load moderation queue',
      retry: 'Retry',
    },

    // Date formatting
    dateTime: {
      justNow: 'Just now',
      minutesAgo: (minutes: number) => `${minutes} minute${minutes === 1 ? '' : 's'} ago`,
      hoursAgo: (hours: number) => `${hours} hour${hours === 1 ? '' : 's'} ago`,
    },

    // Drinks Management
    drinksManagement: {
      setAsDefault: 'Set as Default',
      defaultLabel: 'Default',
      setAsDefaultConfirm: 'Set this drink as the default?',
      setAsDefaultSuccess: 'Default drink updated successfully',
      setAsDefaultError: 'Failed to set as default',
      settingDefault: 'Setting as default...',
    },

    // Event Management
    eventManagement: {
      title: 'Event Management',
      subtitle: 'Create and manage matcha-related events',
      addNewEvent: 'Add New Event',
      editEvent: 'Edit Event',
      createEvent: 'Create Event',
      searchPlaceholder: 'Search events...',
      noEventsFound: 'No events found',
      exportJson: 'Export JSON',
      importJson: 'Import JSON',
      copyToClipboard: 'Copy to Clipboard',
      preview: 'Preview',
      viewDetailsPage: 'View Details Page',
      makeFeatured: 'Make Featured',
      removeFeatured: 'Remove Featured',
      publish: 'Publish',
      unpublish: 'Unpublish',
      deleteEvent: 'Delete Event',
      confirmDelete: 'Are you sure you want to delete this event?',
      confirmMakeFeatured: 'Make this the featured event? This will remove featured status from other events.',
      jsonCopied: 'Event JSON copied to clipboard!',
      jsonExported: 'Event exported as JSON',
      importSuccess: (count: number) => `Successfully imported ${count} event${count !== 1 ? 's' : ''}`,
      importError: 'Failed to import events',
      linkField: 'Link (Instagram Handle or Post URL)',
      linkFieldPlaceholder: '@username or https://instagram.com/p/...',
      selectCafe: 'Link to Cafe (Optional)',
      selectCafePlaceholder: 'Choose a cafe...',
      noCafeLinked: 'No cafe linked',
      moreOptionsAriaLabel: 'More options',
    },
  },

  // Reviews & Rating (Phase 2B)
  reviews: {
    writeReview: 'Write a Review',
    overallRating: 'Overall Rating',
    overallRatingRequired: 'Overall Rating *',
    aspectRatings: 'Detailed Ratings (Optional)',
    matchaQuality: 'Matcha Quality',
    ambiance: 'Ambiance',
    service: 'Service',
    value: 'Value',
    title: 'Review Title',
    titlePlaceholder: 'Sum up your experience...',
    yourReview: 'Your Review',
    contentPlaceholder: 'Share your experience with the matcha, ambiance, service, and overall visit...',
    tags: 'Tags',
    visitDate: 'Visit Date',
    addPhotos: 'Add Photos',
    photosOptional: 'Photos (Optional)',
    makePublic: 'Make this review public',
    submitReview: 'Submit Review',
    submitting: 'Submitting...',
    charCount: (current: number, max: number) => `${current}/${max} characters`,
    minCharacters: (min: number) => `Minimum ${min} characters`,
    maxPhotos: (max: number) => `Up to ${max} photos`,
    photoUploadProgress: (current: number, total: number) => `Uploading photo ${current} of ${total}...`,
    removePhoto: 'Remove photo',
    ratingRequired: 'Please select an overall rating',
    contentTooShort: (min: number) => `Review must be at least ${min} characters`,
    contentTooLong: (max: number) => `Review must be no more than ${max} characters`,
    submitSuccess: 'Review submitted successfully!',
    submitSuccessMessage: 'Thank you for sharing your experience!',
    submitError: 'Failed to submit review. Please try again.',
    photoUploadError: 'Failed to upload photos. Please try again.',
    
    // Aggregated Ratings & Display
    expertScore: 'Expert Score',
    userScore: 'User Score',
    communityRating: 'Community Rating',
    basedOnReviews: (count: number) => `Based on ${count} review${count === 1 ? '' : 's'}`,
    noReviews: 'No reviews yet',
    
    // Review List
    sortBy: 'Sort by',
    sortRecent: 'Most Recent',
    sortRating: 'Highest Rating',
    sortHelpful: 'Most Helpful',
    filterBy: 'Filter by Rating',
    allRatings: 'All Ratings',
    ratingRange: (min: number, max: number) => `${min}-${max} stars`,
    loadMore: 'Load More Reviews',
    noReviewsFound: 'No reviews found',
    
    // Review Card
    helpful: 'Helpful',
    markHelpful: 'Mark as helpful',
    visitedOn: 'Visited on',
    ago: (time: string) => `${time} ago`,
    viewProfile: 'View profile',
    showPhotos: 'Show photos',
    hidePhotos: 'Hide photos',
    featuredReview: 'Featured Review',

    // Error States
    loadError: 'Failed to load reviews',
    tryAgain: 'Please try again',
    retry: 'Retry',

    // Comments (Phase 2F)
    comments: 'Comments',
    addComment: 'Add a comment',
    writeComment: 'Write a comment...',
    reply: 'Reply',
    replyTo: (username: string) => `Reply to ${username}`,
    showReplies: (count: number) => `Show ${count} repl${count === 1 ? 'y' : 'ies'}`,
    hideReplies: 'Hide replies',
    edit: 'Edit',
    delete: 'Delete',
    like: 'Like',
    unlike: 'Unlike',
    likes: (count: number) => `${count} like${count === 1 ? '' : 's'}`,
    noComments: 'No comments yet',
    beFirstToComment: 'Be the first to comment!',
    commenting: 'Commenting...',
    commentAdded: 'Comment added successfully',
    commentUpdated: 'Comment updated successfully',
    commentDeleted: 'Comment deleted successfully',
    commentError: 'Failed to post comment. Please try again.',
    deleteConfirm: 'Are you sure you want to delete this comment?',
    commentTooShort: 'Comment cannot be empty',
    commentTooLong: (max: number) => `Comment must be less than ${max} characters`,
    loadCommentsError: 'Failed to load comments',
    postComment: 'Post Comment',
    cancel: 'Cancel',
    save: 'Save',
  },

  // Photos & Gallery (Phase 2C)
  photos: {
    title: 'Photos',
    noPhotos: 'No photos yet',
    beFirst: 'Be the first to share a photo!',
    showMore: (count: number) => `Show ${count} more photo${count !== 1 ? 's' : ''}`,
    lightbox: {
      close: 'Close photo',
      previous: 'Previous photo',
      next: 'Next photo',
      viewFullSize: 'View full size',
      caption: 'Photo caption',
      uploadedBy: 'Uploaded by',
      uploadedOn: 'Uploaded on',
      downloadOriginal: 'Download original',
    },
    upload: {
      title: 'Upload Photo',
      selectFile: 'Select Photo',
      dragDrop: 'Drag and drop a photo here, or click to select',
      fileTypes: 'JPEG, PNG, or WebP (max 5MB)',
      caption: 'Caption (optional)',
      captionPlaceholder: 'Describe your photo...',
      uploading: 'Uploading...',
      success: 'Photo uploaded successfully!',
      pending: 'Your photo is pending moderation and will appear soon.',
      error: 'Failed to upload photo',
      submit: 'Upload Photo',
      cancel: 'Cancel',
      invalidFileType: 'Invalid file type. Please upload a JPEG, PNG, or WebP image.',
      fileTooLarge: 'File too large. Maximum size is 5MB.',
      emptyFile: 'File is empty. Please select a valid image.',
    },
    myPhotos: {
      title: 'My Photos',
      noPhotos: 'You haven\'t uploaded any photos yet.',
      startVisiting: 'Start by visiting a cafe and sharing your experience!',
      status: {
        pending: 'Pending Review',
        approved: 'Approved',
        rejected: 'Rejected',
      },
      delete: 'Delete Photo',
      confirmDelete: 'Are you sure you want to delete this photo?',
      deleteSuccess: 'Photo deleted successfully',
      deleteError: 'Failed to delete photo',
    },
    moderation: {
      title: 'Photo Moderation',
      queue: 'Moderation Queue',
      noPending: 'No photos pending moderation',
      allReviewed: 'All photos have been reviewed. New uploads will appear here.',
      approve: 'Approve',
      reject: 'Reject',
      notes: 'Notes (optional)',
      notesPlaceholder: 'Add notes about your decision...',
      approveSuccess: 'Photo approved',
      rejectSuccess: 'Photo rejected',
      moderationError: 'Failed to moderate photo',
      uploadedBy: 'Uploaded by',
      uploadedOn: 'on',
      cafe: 'Cafe',
      fileSize: 'File size',
      dimensions: 'Dimensions',
    },
  },

  // Check-ins (Phase 2C)
  checkin: {
    // Button Text
    checkIn: 'Check In',
    checkedIn: 'Checked In',
    checkingIn: 'Checking in...',

    // Modal
    title: 'Check in to',
    subtitle: 'Share your experience at this cafe',
    notesLabel: 'Notes (Optional)',
    notesPlaceholder: 'How was your visit? Any recommendations?',
    photosLabel: 'Photos (Optional)',
    ratingLabel: 'Quick Rating',
    ratingOptional: '(Optional)',
    submitButton: 'Check In',
    submittingButton: 'Checking in...',
    cancel: 'Cancel',

    // Success States
    successTitle: 'Checked in successfully!',
    successMessage: 'Your check-in has been recorded.',
    successClose: 'Close',

    // Error States
    errorTitle: 'Check-in Failed',
    errorMessage: 'Unable to record your check-in. Please try again.',
    errorRetry: 'Try Again',
    errorClose: 'Close',

    // Photo Upload
    addPhotos: 'Add Photos',
    removePhoto: 'Remove photo',
    photoUploading: 'Uploading photo...',
    photoError: 'Failed to upload photo',

    // Rating
    ratingPrompt: 'Rate your experience',
    stars: (count: number) => `${count} star${count !== 1 ? 's' : ''}`,
    clearRating: 'Clear rating',

    // Character Limits
    notesMaxLength: 500,
    notesCharCount: (current: number, max: number) => `${current}/${max}`,
    notesTooLong: (max: number) => `Notes must be ${max} characters or less`,

    // Validation
    alreadyCheckedIn: 'You\'ve already checked in to this cafe today',
    networkError: 'Check your internet connection and try again',
  },

  // Social Features (Phase 2D)
  social: {
    // Follow Actions
    follow: 'Follow',
    following: 'Following',
    unfollow: 'Unfollow',
    followUser: (username: string) => `Follow ${username}`,
    unfollowUser: (username: string) => `Unfollow ${username}`,
    
    // Follow Lists
    followers: 'Followers',
    followersTitle: (username: string) => `${username}'s Followers`,
    followingTitle: (username: string) => `${username} is Following`,
    followersCount: (count: number) => `${count} ${count === 1 ? 'follower' : 'followers'}`,
    followingCount: (count: number) => `${count} following`,
    
    // Empty States
    noFollowers: 'No followers yet',
    noFollowing: 'Not following anyone yet',
    noFollowersDescription: 'When people follow this user, they\'ll appear here.',
    noFollowingDescription: 'When this user starts following people, they\'ll appear here.',
    
    // Loading States
    loadingUsers: 'Loading users...',
    
    // Follow Status
    followingYou: 'Follows you',
    mutualFollow: 'You follow each other',
    followBack: 'Follow back',
  },

  // Badges & Achievements (Phase 2C)
  badges: {
    // Main Badge Interface
    title: 'Badges',
    myBadges: 'My Badges',
    earnedBadges: 'Earned Badges',
    badgeProgress: 'Progress',
    allBadges: 'All Badges',
    noBadges: 'No badges yet',
    earnBadges: 'Start visiting cafes and writing reviews to earn badges!',
    
    // Badge Categories
    categories: {
      passport: 'Passport',
      reviews: 'Reviews',
      photos: 'Photos',
      special: 'Special',
    },

    // Badge Notifications
    newBadge: 'New Badge Earned!',
    congratulations: 'Congratulations!',
    youEarned: 'You earned the',
    badge: 'badge',
    viewBadge: 'View Badge',
    viewAllBadges: 'View All Badges',
    dismiss: 'Dismiss',
    
    // Progress Display
    progressToNext: 'Progress to next badge',
    almostThere: 'Almost there!',
    nextBadge: 'Next badge',
    currentProgress: (current: number, target: number) => `${current} of ${target}`,
    progressPercent: (percent: number) => `${Math.round(percent * 100)}% complete`,
    
    // Common Badge Actions
    checkProgress: 'Check Progress',
    checkingProgress: 'Checking...',
    progressChecked: 'Progress updated!',
    noNewBadges: 'No new badges to award',
    
    // Badge Display
    earnedOn: 'Earned on',
    justEarned: 'Just earned!',
    shareAchievement: 'Share Achievement',
    badgeDescription: 'Badge Description',
    requirements: 'Requirements',
    yourProgress: 'Your Progress',
    completed: 'Completed',
    
    // Error States
    loadError: 'Failed to load badges',
    checkError: 'Failed to check for new badges',
    tryAgain: 'Please try again',
    retry: 'Retry',
    
    // Placeholder Text
    comingSoon: 'More badges coming soon!',
    keepExploring: 'Keep exploring to unlock more achievements.',
    
    // Specific Badge Names (from backend definitions)
    badgeNames: {
      passport_5: 'Explorer',
      passport_10: 'Adventurer',
      passport_25: 'Neighborhood Expert',
      passport_50: 'City Explorer',
      passport_100: 'Matcha Master',
      reviews_10: 'Reviewer',
      reviews_25: 'Critic',
      reviews_50: 'Expert Reviewer',
      reviews_100: 'Review Master',
      photos_10: 'Photographer',
      photos_50: 'Visual Storyteller',
      photos_100: 'Photo Master',
      early_adopter: 'Early Adopter',
    },
    
    // Achievement Messages
    achievementMessage: {
      passport: 'Great job exploring the matcha scene!',
      reviews: 'Thanks for sharing your experiences!',
      photos: 'Beautiful photos! Keep sharing.',
      special: 'You\'re part of the MatchaMap family!',
    },
  },

  // Leaderboards (Phase 2D)
  leaderboard: {
    title: 'Leaderboards',
    subtitle: 'Discover the top community members',
    
    // Tab Labels
    passport: 'Passport',
    reviewers: 'Reviewers',
    contributors: 'Contributors',
    
    // Period Filters
    allTime: 'All Time',
    monthly: 'This Month',
    
    // City Filter
    allCities: 'All Cities',
    filterByCity: 'Filter by city',
    
    // Headers
    rank: 'Rank',
    user: 'User',
    stats: 'Stats',
    
    // Passport Leaderboard
    passportTitle: 'Top Explorers',
    passportDescription: 'Members who have visited the most cafes',
    checkinsCount: (count: number) => `${count} check-ins`,
    passportProgress: (percent: number) => `${percent}% complete`,
    
    // Reviewer Leaderboard
    reviewerTitle: 'Top Reviewers',
    reviewerDescription: 'Members who have written the most helpful reviews',
    reviewsCount: (count: number) => `${count} reviews`,
    reputationScore: (score: number) => `${score} reputation`,
    
    // Contributor Leaderboard
    contributorTitle: 'Top Contributors',
    contributorDescription: 'Members with the most community contributions',
    contributionsCount: (count: number) => `${count} contributions`,
    contributionBreakdown: (reviews: number, photos: number, favorites: number) => 
      `${reviews} reviews, ${photos} photos, ${favorites} favorites`,
    
    // User Rank
    yourRank: 'Your Rank',
    yourRankDescription: (rank: number, type: string) => `You are ranked #${rank} in ${type}`,
    notRanked: 'Not ranked yet',
    notRankedDescription: 'Start contributing to appear on the leaderboard',
    
    // Empty States
    noData: 'No data available',
    noDataDescription: 'Be the first to appear on this leaderboard!',
    
    // Loading States
    loading: 'Loading leaderboard...',
    loadingRank: 'Loading your rank...',
    
    // Error States
    errorTitle: 'Unable to load leaderboard',
    errorDescription: 'Please try again later',
    retry: 'Retry',
    
    // Accessibility
    leaderboardTable: 'Leaderboard rankings table',
    userAvatar: (username: string) => `${username}'s avatar`,
    sortByRank: 'Sorted by rank',
  },

  // Cafe Suggestions
  suggestions: {
    // Form
    title: 'Suggest a Cafe',
    description: 'Know a great matcha spot we should add? Share it with us!',
    nameLabel: 'Cafe Name',
    namePlaceholder: 'Enter cafe name',
    addressLabel: 'Address',
    addressPlaceholder: 'Street address, city, province',
    cityLabel: 'City',
    cityPlaceholder: 'Select a city',
    neighborhoodLabel: 'Neighborhood (optional)',
    neighborhoodPlaceholder: 'e.g., Queen West, Little Italy',
    descriptionLabel: 'Description (optional)',
    descriptionPlaceholder: 'Tell us what makes this cafe special...',
    googleMapsLabel: 'Google Maps URL (optional)',
    googleMapsPlaceholder: 'https://maps.google.com/...',
    instagramLabel: 'Instagram Handle (optional)',
    instagramPlaceholder: '@cafename',
    websiteLabel: 'Website (optional)',
    websitePlaceholder: 'https://example.com',
    submitButton: 'Submit Suggestion',
    submitting: 'Submitting...',
    cancelButton: 'Cancel',

    // Validation
    nameRequired: 'Cafe name is required',
    nameMinLength: 'Cafe name must be at least 2 characters',
    nameMaxLength: 'Cafe name must be less than 200 characters',
    addressRequired: 'Address is required',
    addressMinLength: 'Address must be at least 5 characters',
    addressMaxLength: 'Address must be less than 500 characters',
    cityRequired: 'City is required',
    descriptionMinLength: 'Description must be at least 10 characters if provided',
    descriptionMaxLength: 'Description must be less than 1000 characters',

    // Success/Error
    successTitle: 'Suggestion Submitted!',
    successMessage: 'Thanks for your suggestion! We\'ll review it and add it to the map if it\'s a good fit.',
    errorTitle: 'Submission Failed',
    errorMessage: 'Unable to submit your suggestion. Please try again.',

    // My Suggestions
    mySuggestionsTitle: 'My Suggestions',
    mySuggestionsEmpty: 'You haven\'t suggested any cafes yet',
    statusPending: 'Pending Review',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    submittedOn: (date: string) => `Submitted on ${date}`,

    // Admin
    adminTitle: 'Pending Cafe Suggestions',
    adminEmpty: 'No pending suggestions',
    adminDescription: 'Review and moderate user-submitted cafe suggestions',
    approveButton: 'Approve',
    rejectButton: 'Reject',
    adminNotesLabel: 'Admin Notes (optional)',
    adminNotesPlaceholder: 'Internal notes about this suggestion...',
    cafeIdLabel: 'Link to Cafe (optional)',
    cafeIdPlaceholder: 'Enter cafe ID if already created',
    approving: 'Approving...',
    rejecting: 'Rejecting...',
    approvedSuccess: 'Suggestion approved!',
    rejectedSuccess: 'Suggestion rejected',
    moderatedBy: (username: string) => `Moderated by ${username}`,
    suggestedBy: (username: string) => `Suggested by ${username}`,
  },

  // Notifications
  notifications: {
    title: 'Notifications',
    empty: 'No notifications yet',
    emptyDescription: 'When someone follows you, comments on your reviews, or finds them helpful, you\'ll see it here',
    markAllRead: 'Mark all as read',
    markAsRead: 'Mark as read',
    viewAll: 'View All',
    newNotification: 'New notification',
    unreadCount: (count: number) => count === 1 ? '1 unread' : `${count} unread`,

    // Notification types
    follower: (username: string) => `${username} started following you`,
    comment: (username: string) => `${username} commented on your review`,
    commentLike: (username: string) => `${username} liked your comment`,
    helpful: (username: string) => `${username} found your review helpful`,
    badge: (badgeName: string) => `You earned the "${badgeName}" badge!`,

    // Time formatting
    justNow: 'Just now',
    minutesAgo: (minutes: number) => minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`,
    hoursAgo: (hours: number) => hours === 1 ? '1 hour ago' : `${hours} hours ago`,
    daysAgo: (days: number) => days === 1 ? '1 day ago' : `${days} days ago`,
    weeksAgo: (weeks: number) => weeks === 1 ? '1 week ago' : `${weeks} weeks ago`,

    // Actions
    loading: 'Loading notifications...',
    error: 'Failed to load notifications',
    retry: 'Retry',
  },
} as const

// Type helper to ensure type safety when accessing COPY
export type CopyKeys = typeof COPY
